/**
 * Environment configuration utility
 * Provides type-safe access to environment variables with validation
 */

interface EnvironmentConfig {
  supabase: {
    url: string;
    anonKey: string;
  };
  stripe: {
    publishableKey?: string;
  };
  app: {
    env: 'development' | 'staging' | 'production';
    isDevelopment: boolean;
    isProduction: boolean;
    isStaging: boolean;
  };
}

// Public Supabase config — safe to commit as build-time fallbacks.
//
// The project ref/URL are already public (committed in supabase/config.toml) and
// the anon / "publishable" key is designed to ship in the client bundle (it
// reaches every browser and is gated by row-level security). These act as
// fallbacks so the prerender/SSR build never fails when a host's env vars drift;
// host-provided VITE_* values always take precedence over them.
const PUBLIC_SUPABASE_PROJECT_REF = 'unhkgjpswurbpzohqlfo';
const PUBLIC_SUPABASE_URL = `https://${PUBLIC_SUPABASE_PROJECT_REF}.supabase.co`;
const PUBLIC_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVuaGtnanBzd3VyYnB6b2hxbGZvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI4MjkyMDIsImV4cCI6MjA2ODQwNTIwMn0.qb9SjEy1vJef7ef6tZXui176ej8riY6aUX91OmUaUv4';

function resolveEnvVar(names: string[], fallback?: string): string {
  for (const key of names) {
    const value = import.meta.env[key];
    if (value) return value;
  }
  if (fallback !== undefined) return fallback;
  throw new Error(`Missing required environment variable: ${names[0]}`);
}

function getOptionalEnvVar(name: string): string | undefined {
  return import.meta.env[name];
}

export const env: EnvironmentConfig = {
  supabase: {
    url: resolveEnvVar(['VITE_SUPABASE_URL'], PUBLIC_SUPABASE_URL),
    // Supabase renamed the public client key "anon" -> "publishable"; accept
    // either name, and fall back to the committed public key so the build works
    // regardless of which (if any) the host env defines.
    anonKey: resolveEnvVar(
      ['VITE_SUPABASE_ANON_KEY', 'VITE_SUPABASE_PUBLISHABLE_KEY'],
      PUBLIC_SUPABASE_ANON_KEY,
    ),
  },
  stripe: {
    publishableKey: getOptionalEnvVar('VITE_STRIPE_PUBLISHABLE_KEY'),
  },
  app: {
    env: (getOptionalEnvVar('VITE_APP_ENV') as EnvironmentConfig['app']['env']) || 'development',
    get isDevelopment() {
      return this.env === 'development';
    },
    get isProduction() {
      return this.env === 'production';
    },
    get isStaging() {
      return this.env === 'staging';
    },
  },
};

// Validate critical environment variables on import
if (!env.supabase.url || !env.supabase.anonKey) {
  throw new Error('Missing required Supabase environment variables');
}

export default env; 